import React from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { useMutation } from '@apollo/react-hooks';
import delve from 'dlv';
import dayjs from 'dayjs';
import log from 'loglevel';

import { useQuizExceptionTracking } from 'Util/Analytics';

import { QueryComponent } from '../../GraphQL';
import { getClassroomQuizSessions } from '../../GraphQL/query/Classroom.query';
import { enterClassroomQuizSession } from '../../GraphQL/mutation/Classroom.mutation';
import { useClassroomContext } from '../../Context/ClassroomContext';
import { useQuizContext } from '../../Context/QuizContext';
import Loading from '../../Components/Loading';

/**
 * @func formatQuizSessions
 * @description Sort sessions by startedAt && add "round" field to each session
 * @param {Object[]} sessions
 * @return {Object[]}
 */
const formatQuizSessions = (sessions) => {
  const formattedSesssions = sessions
    .map((session, index) => {
      const { id, startedAt, score, fullScore } = session;
      const date = dayjs(startedAt).format('DD MMM YY / HH:mm');
      const altDate = dayjs(startedAt).format('DD/MM/YY | HH:mm');
      return { id, date, altDate, score, fullScore, round: index + 1 };
    })
    .sort((a, b) => {
      return b.round - a.round;
    });

  return formattedSesssions;
};

/**
 * This container component provides classroomQuizSession's data and functions to children component.
 * It needs to be used under ClassroomProvider and QuizProvider.
 */
const ClassroomQuizSession = withApollo(
  ({
    metadata,
    classroomCourseId,
    courseId,
    courseData,
    courseAssetId,
    examId,
    setError,
    client,
    children,
  }) => {
    const { logStartQuiz, logStartQuizError } = useQuizExceptionTracking();
    const { title, type } = metadata;

    const [startSession, { loading, data: classroomSessionData }] = useMutation(
      enterClassroomQuizSession,
      {
        client,
        variables: { classroomCourseId, courseAssetId, examId },
        onCompleted() {
          logStartQuiz({ quizId: examId, subsectionTitle: title, subsectionContentType: type });
        },
        onError(error) {
          log.error(`enterClassroomQuizSession error: `, error);
          logStartQuizError({
            quizId: examId,
            errorMessage: error.message,
            subsectionTitle: title,
            subsectionContentType: type,
          });
          setError({
            message: 'ขออภัย ไม่สามารถทำแบบฝึกหัดได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
            onContinue: () => {},
          });
        },
      }
    );

    const examSessionId = delve(
      classroomSessionData,
      ['enterClassroomQuizSession', 'session', 'id'],
      null
    );

    return (
      <QueryComponent
        query={getClassroomQuizSessions}
        variables={{ classroomCourseId, courseAssetId, examId }}
        fetchPolicy={'network-only'}
        renderLoading={() => <Loading />}
      >
        {(data) => {
          const quizSessions = delve(data, ['classroomQuizSessions', 'sessions'], []);
          return children({
            quizSessions: formatQuizSessions(quizSessions),
            startSession,
            examSessionId,
            isLoadingQuizSession: loading,
          });
        }}
      </QueryComponent>
    );
  }
);

ClassroomQuizSession.propTypes = {
  classroomCourseId: PropTypes.string.isRequired,
  courseAssetId: PropTypes.string.isRequired,
  children: PropTypes.func,
};

ClassroomQuizSession.defaultProps = {
  children: () => {},
};

const ClassroomQuizSessionWrapper = (props) => {
  const { classroomCourseId, courseId, courseData } = useClassroomContext();
  const { courseAssetId, examId, setError, metadata } = useQuizContext();
  return (
    <ClassroomQuizSession
      classroomCourseId={classroomCourseId}
      courseAssetId={courseAssetId}
      examId={examId}
      setError={setError}
      courseId={courseId}
      courseData={courseData}
      metadata={metadata}
      {...props}
    />
  );
};

export default ClassroomQuizSessionWrapper;
